import { RefreshCw } from 'lucide-react';
import React, { ReactNode, Suspense, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ErrorChartCloudIcon,
  ErrorFolderCloudIcon,
  ErrorIcon,
  ErrorTableCloudIcon,
} from '@/components/error-handling/error-icons';
import type { ChartErrorChartProps } from '@/components/error-handling/chart-error-fallback';
import { isChunkLoadError } from '@/components/error-handling/helpers/is-chunk-load-error';
import { tryRecoverFromChunkError } from '@/components/error-handling/hooks/use-chunk-error-recovery';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { trackError } from '@/lib/monitoring/track-error';
import { cn } from '@/lib/utils';

// Lazy-load chart error UI (MEASURES_MAP + HeaderSlot) only when a chart
// boundary actually fails — keeps those deps out of the authenticated shell.
const ChartErrorFallback = React.lazy(async () => {
  const chartErrorFallbackModule = await import('@/components/error-handling/chart-error-fallback');
  return { default: chartErrorFallbackModule.ChartErrorFallback };
});

enum ERROR_PAGE_TYPE {
  DEFAULT = 'default',
  CHART = 'chart',
  TABLE = 'table',
  FOLDER = 'folder',
}

enum ERROR_ICON_DISPLAY_TYPE {
  CHART = 'chart',
  CHART_SMALL = 'chart-small',
  TABLE = 'table',
  FOLDER = 'folder',
  DEFAULT = 'default',
}

interface ErrorPageContainerProps {
  children: ReactNode;
  fallback?: ReactNode;
  errorTitle?: string;
  errorDescription?: string;
  type?: `${ERROR_PAGE_TYPE}`;
  className?: string;
  chartProps?: ChartErrorChartProps;
}

type ErrorIconDisplayType = `${ERROR_ICON_DISPLAY_TYPE}`;

function ErrorIconsDisplay({ type }: { type: ErrorIconDisplayType }) {
  switch (type) {
    case 'chart':
    case 'chart-small':
      return (
        <ErrorChartCloudIcon
          className={cn('h-[92px] w-[112px]', type === 'chart-small' && 'h-[58px] w-[70px]')}
        />
      );
    case 'table':
      return <ErrorTableCloudIcon className="h-[100px] w-[140px]" />;
    case 'folder':
      return <ErrorFolderCloudIcon />;
    default:
      return <ErrorIcon className="size-60" />;
  }
}

function ErrorPageContainer({
  children,
  type = ERROR_PAGE_TYPE.DEFAULT,
  fallback = (
    <div className="flex h-full w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="large" type="gradient" />
        <p className="text-sm text-foreground/70">Loading...</p>
      </div>
    </div>
  ),
  errorTitle = 'Unable to load the data',
  errorDescription = 'This may occur due to no internet or an unavailable component. Try refreshing, checking your connection, or retrying later.',
  className,
  chartProps,
}: ErrorPageContainerProps) {
  const [boundaryState, setBoundaryState] = useState<{
    error: Error | null;
    hasError: boolean;
    key: number;
  }>({ error: null, hasError: false, key: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  const childrenRef = React.useRef(children);

  // Reset when routed content changes while an error is showing (e.g. navigation).
  useEffect(() => {
    if (childrenRef.current === children) return;
    childrenRef.current = children;
    setBoundaryState(prev => {
      if (!prev.hasError) return prev;
      return { error: null, hasError: false, key: prev.key + 1 };
    });
  }, [children]);

  const handleReset = () => {
    if (isChunkLoadError(boundaryState.error)) {
      window.location.reload();
      return;
    }

    setBoundaryState(prev => ({ error: null, hasError: false, key: prev.key + 1 }));

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('refresh', Date.now().toString());

    void navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
      state: { refreshedAt: Date.now() },
    });
  };

  const onError = (capturedError: Error, errorInfo?: React.ErrorInfo) => {
    if (tryRecoverFromChunkError(capturedError)) {
      return;
    }

    // Chunk-load failures that exhausted silent reloads still flip hasError so
    // the render branch shows the neutral loading fallback instead of the
    // destructive error UI. Do not report to Sentry — recovery, not a fault.
    if (isChunkLoadError(capturedError)) {
      setBoundaryState(prev => ({
        error: capturedError,
        hasError: true,
        key: prev.key,
      }));
      return;
    }

    // Nested boundary owns the catch — parent AppProvider will not see this.
    // Report once here so Sentry on-error replay can attach.
    trackError(capturedError, {
      component: 'ErrorPageContainer',
      action: 'render-error',
      metadata: {
        url: window.location.href,
        componentStack: errorInfo?.componentStack,
      },
    });

    setBoundaryState(prev => ({
      error: capturedError,
      hasError: true,
      key: prev.key,
    }));
  };

  const { error, hasError, key: errorBoundaryKey } = boundaryState;

  if (hasError) {
    // Chunk-load errors that exhausted silent reloads still must not display
    // alarming copy to customers — keep showing the neutral loading fallback
    // so a future navigation or manual refresh resolves it quietly.
    if (isChunkLoadError(error)) {
      return <>{fallback}</>;
    }

    if (type === ERROR_PAGE_TYPE.CHART) {
      return (
        <Suspense fallback={fallback}>
          <ChartErrorFallback
            error={error}
            errorTitle={errorTitle}
            errorDescription={errorDescription}
            className={className}
            chartProps={chartProps}
            onReset={handleReset}
          />
        </Suspense>
      );
    }

    return (
      <div
        className={cn(
          'flex h-full w-full flex-col items-center justify-center bg-background',
          className
        )}
      >
        <ErrorIconsDisplay type={type} />

        <h3 className="mb-2 text-base font-bold">{errorTitle}</h3>
        <p className="mb-6 max-w-md text-center text-foreground/80">
          {errorDescription}
          {error ? (
            <span className="mt-2 block text-sm opacity-80">{error.message}</span>
          ) : null}
        </p>

        <Button size="sm" onClick={handleReset} className="text-sm font-medium text-foreground/70">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <ErrorBoundary
      key={errorBoundaryKey}
      onError={onError}
      onHasError={nextHasError => {
        setBoundaryState(prev => ({
          ...prev,
          hasError: nextHasError,
          error: nextHasError ? prev.error : null,
        }));
      }}
    >
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
  onError: (error: Error, errorInfo: React.ErrorInfo) => void;
  onHasError: (hasError: boolean) => void;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Reporting lives in the parent onError callback (single path — no double Sentry).
    this.props.onError(error, errorInfo);
    this.props.onHasError(true);
  }

  static getDerivedStateFromProps(props: ErrorBoundaryProps, state: { hasError: boolean }) {
    if (state.hasError && !props.children) {
      return { hasError: false };
    }
    return null;
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-lg text-foreground/90">Something went wrong loading this section.</p>
          <button
            type="button"
            className="mt-4 text-sm text-primary underline"
            onClick={() => this.setState({ hasError: false })}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export { ErrorPageContainer };
